import { useCallback } from "react"
import type { Partner, PartnerType } from "@stockline/shared/types/crm/partner"
import { PARTNER_TABLE } from "../../../lib/offline/resources/crm/partner/db"
import { useAISync } from "../../ai/providers/AISyncProvider"

export interface CreatePartnerInput {
  name: string
  type: PartnerType
  usageName?: string | null
  code?: string | null
  aiContext?: string | null
  note?: string | null
  isActive?: boolean
  /**
   * Optional initial client-group memberships. When present, the partner
   * push-batch handler runs an atomic delete + insert against
   * `partner_client_groups` in the same transaction as the partner INSERT —
   * see `apps/api/src/domains/crm/partner/repository.ts`.
   */
  clientGroupIds?: string[]
}

export interface UpdatePartnerInput {
  name?: string
  type?: PartnerType
  usageName?: string | null
  code?: string | null
  aiContext?: string | null
  note?: string | null
  isActive?: boolean
  /**
   * Pass to atomically re-set the partner's client-group memberships. Server
   * deletes existing rows for (companyId, partnerId) and inserts one per id;
   * the partners trigger bumps sync_id on the parent row so a single
   * `partner.updated` SSE event carries both the header churn AND the
   * refreshed clientGroupIds[] projection.
   */
  clientGroupIds?: string[]
}

export function usePartnerMutations() {
  const { storage, changeQueue, engine } = useAISync()

  const createPartner = useCallback(
    async (input: CreatePartnerInput): Promise<Partner> => {
      const id = crypto.randomUUID()
      const now = new Date().toISOString()

      const partner: Partner = {
        id,
        companyId: "",
        name: input.name,
        usageName: input.usageName ?? null,
        code: input.code ?? null,
        type: input.type,
        aiContext: input.aiContext ?? null,
        note: input.note ?? null,
        partnerMetadata: null,
        isActive: input.isActive ?? true,
        deletedAt: null,
        syncId: 0,
        createdAt: now,
        updatedAt: now,
        // Enrichment fields populated server-side once the partner syncs back.
        // Use the caller-provided clientGroupIds for the optimistic write so
        // the partner immediately appears in any group-filtered view; the
        // next sync replaces this with the server-authoritative projection.
        clientGroupIds: input.clientGroupIds ?? [],
      }

      await storage.put(PARTNER_TABLE, partner)
      await changeQueue.enqueue("partners", {
        op: "create",
        id,
        data: partner,
      })
      void engine.pushChanges("partners")

      return partner
    },
    [storage, changeQueue, engine]
  )

  const updatePartner = useCallback(
    async (id: string, input: UpdatePartnerInput): Promise<void> => {
      const existing = await storage.get<Partner>(PARTNER_TABLE, id)
      if (!existing) return

      const updated: Partner = {
        ...existing,
        ...input,
        // clientGroupIds is intentionally a full replacement (atomic re-set),
        // not a merge — matches the server-side membership re-set semantics.
        // The spread above already overwrites it when present in `input`.
        updatedAt: new Date().toISOString(),
      }

      await storage.put(PARTNER_TABLE, updated)
      await changeQueue.enqueue("partners", {
        op: "update",
        id,
        data: input,
      })
      void engine.pushChanges("partners")
    },
    [storage, changeQueue, engine]
  )

  const deletePartner = useCallback(
    async (id: string): Promise<void> => {
      await storage.delete(PARTNER_TABLE, id)
      await changeQueue.enqueue("partners", {
        op: "delete",
        id,
      })
      void engine.pushChanges("partners")
    },
    [storage, changeQueue, engine]
  )

  return { createPartner, updatePartner, deletePartner }
}
